<template>

  <!--
    noForm renders the bare select (no el-form-item wrapper). el-form-item
    injects `elForm` from an ancestor <el-form>; used outside one (e.g. inside
    an el-popover) it throws during render and Vue drops the select to a comment
    node. Callers with no surrounding form pass :no-form="true".
  -->
  <component :is="noForm ? 'div' : 'el-form-item'" :label="noForm ? undefined : label">
    <el-select
      v-model="selected"
      :placeholder="placeholder"
      :size="size"
      :disabled="disabled"
      :clearable="clearable"
      :filterable="filterable"
      :loading="loading"
      loading-text="Loading rates…"
      :no-data-text="noDataText || 'No data'"
      no-match-text="No rates match your search"
      value-key="id"
      @change="onChange">
      <el-option
        v-for="item in displayOptions"
        :key="item.id"
        :value="item.id"
        :label="item.name" />
    </el-select>
  </component>

</template>

<script>

import axios from '@/lib/axiosClient'

import PostMixins from '@/mixins/PostMixins'

export default {
  name: 'select-sharer-rates',

  mixins: [PostMixins],

  props: {

    value: {
      default: null
    },

    label: {
      type: String,
      default: 'Sharer Rates'
    },

    sharerId: {
      type: Number,
      default: null
    },

    disabled: {
      type: Boolean,
      default: false
    },

    required: {
      type: Boolean,
      default: false
    },

    size: {
      type: String,
      default: 'default'
    },

    placeholder: {
      type: String,
      default: null
    },

    helpText: {
      type: String,
      default: null
    },

    noForm: {
      type: Boolean,
      default: false
    },

    clearable: {
      type: Boolean,
      default: false
    },

    // Type-to-search autocomplete over the rate list (filters by option label).
    filterable: {
      type: Boolean,
      default: true
    },

    noDataText: {
      type: String,
      default: null
    }
  },

  methods: {
    // Fetch the sharer-rate options scoped to this sharer org via the backend
    // by-org endpoint (XOL-241 / API PR #95): GET /rates/sharer/by-org/{orgId}
    // returns the org's OWN rates PLUS generic/unassigned ones, already filtered
    // server-side. It is paginate(20), so page through and accumulate.
    fetchScopedRates: function () {
      if (!this.sharerId) {
        this.options = []
        this.loading = false
        return
      }

      var self = this
      var orgId = this.sharerId
      var acc = []
      // Safety cap against a runaway loop; far above the expected ~7 pages
      // (own + ~108 generic rates at paginate(20)).
      var MAX_PAGES = 50

      // Show the el-select loading text while paging — on a slow connection the
      // multi-page fetch was previously surfacing the empty "no rates" text,
      // which read as "this sharer has no rates" rather than "still loading".
      this.loading = true

      var getPage = function (page) {
        axios.get(self.getEndpoint('rates/sharer/by-org/' + orgId + '?page=' + page))
          .then(function (resp) {
            var body = resp.data || {}
            // Laravel paginator: { current_page, last_page, data: [...] }
            var rows = body.data || []
            acc = acc.concat(rows)

            var cur = body.current_page || page
            var last = body.last_page || cur
            // Guard against a stale response if the sharer changed mid-fetch —
            // a newer fetch now owns `loading`, so don't touch it here.
            if (self.sharerId !== orgId) return
            if (cur < last && page < MAX_PAGES) {
              getPage(page + 1)
            } else {
              self.options = acc
              self.loading = false
            }
          })
          .catch(function (err) {
            console.error('SelectSharerRates: by-org fetch failed', err)
            if (self.sharerId === orgId) {
              self.options = acc
              self.loading = false
            }
          })
      }

      getPage(1)
    },

    onChange: function (val) {
      this.$emit('input', val)
      this.$emit('change', val)
    },

    // Inject a newly-created rate (A2: after duplicate-save) into the options
    // so it appears without a re-fetch.
    addOption: function (rate) {
      if (!rate || !rate.id) return
      if (!this.options.some(function (o) { return o.id === rate.id })) {
        this.options = this.options.concat([rate])
      }
    }
  },

  computed: {
    // el-select renders the SELECTED value's label by matching it to an option.
    // The bound value is the rate id; the label (rate name) lives only in the
    // async by-org option list. Before that list loads, no option matches the
    // id, so el-select shows the raw numeric id. Seed a placeholder option for
    // the selected id until the real one arrives, so the input shows a label
    // ("Loading…" while fetching) instead of the bare number.
    displayOptions: function () {
      var sel = this.selected
      if (sel === null || sel === undefined) return this.options
      var present = this.options.some(function (o) { return o.id === sel })
      if (present) return this.options
      return this.options.concat([{ id: sel, name: this.loading ? 'Loading…' : 'Selected rate' }])
    }
  },

  watch: {
    value: function (val) {
      this.selected = val
    },

    // Sharer changed (SharerAction picks it in-form): re-fetch the scoped list.
    sharerId: function (val, oldVal) {
      if (val === oldVal) return
      this.options = []
      this.fetchScopedRates()
    }
  },

  data: function () {
    return {
      selected: this.value,
      options: [],
      loading: false
    }
  },

  mounted: function () {
    this.fetchScopedRates()
  }
}

</script>

<style scoped>
.el-select { width: 100%; }
</style>
